import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

/** Lado del marco de recorte y de la imagen exportada (px). */
const FRAME = 256;

interface Natural {
  w: number;
  h: number;
}

interface DragStart {
  pointerX: number;
  pointerY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Editor de recorte circular para la foto de avatar. El usuario mueve
 * (arrastrando) y hace zoom dentro de un marco circular; al guardar, el
 * recorte visible se dibuja en un canvas FRAME×FRAME y se exporta a webp
 * (con fallback a jpeg) — todo en el cliente, sin tocar el backend.
 *
 * El render del <img> usa left/top/width/height en px que se replican 1:1
 * en `drawImage`, de modo que lo que se ve es exactamente lo que se guarda.
 */
export function AvatarCropModal({
  file,
  busy,
  onCancel,
  onConfirm,
}: {
  file: File;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [natural, setNatural] = useState<Natural | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<DragStart | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // ObjectURL de la imagen elegida; se revoca al desmontar / cambiar fichero.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Escala base que hace que la imagen CUBRA el marco (cover).
  const baseScale = natural ? Math.max(FRAME / natural.w, FRAME / natural.h) : 1;
  const displayScale = baseScale * zoom;
  const w = natural ? natural.w * displayScale : 0;
  const h = natural ? natural.h * displayScale : 0;

  // Limita el desplazamiento para que la imagen nunca deje hueco en el marco.
  const clamp = useCallback(
    (x: number, y: number) => {
      const maxX = Math.max(0, (w - FRAME) / 2);
      const maxY = Math.max(0, (h - FRAME) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [w, h],
  );

  // Re-encaja el offset cuando cambia el zoom (los límites cambian).
  useEffect(() => {
    setOffset((prev) => clamp(prev.x, prev.y));
  }, [clamp]);

  const topLeftX = FRAME / 2 - w / 2 + offset.x;
  const topLeftY = FRAME / 2 - h / 2 + offset.y;

  function onPointerDown(e: React.PointerEvent) {
    if (!natural) return;
    dragRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    const start = dragRef.current;
    if (!start) return;
    setOffset(
      clamp(start.offsetX + (e.clientX - start.pointerX), start.offsetY + (e.clientY - start.pointerY)),
    );
  }

  function onPointerUp(e: React.PointerEvent) {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  function handleConfirm() {
    const img = imgRef.current;
    if (!img || !natural) return;
    const canvas = document.createElement('canvas');
    canvas.width = FRAME;
    canvas.height = FRAME;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, topLeftX, topLeftY, w, h);
    // webp preferido; si el navegador no lo soporta (Safari antiguo),
    // toBlob devuelve PNG/null → reintento jpeg, que el backend acepta.
    canvas.toBlob(
      (blob) => {
        if (blob && blob.type === 'image/webp') {
          onConfirm(blob);
          return;
        }
        canvas.toBlob((jpeg) => jpeg && onConfirm(jpeg), 'image/jpeg', 0.85);
      },
      'image/webp',
      0.85,
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ajustar foto de perfil"
    >
      <div className="w-full max-w-sm rounded-sm border border-border bg-card shadow-lg">
        <header className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold tracking-tightish">Ajustar foto</h2>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Arrastra para encuadrar y usa el control para acercar.
          </p>
        </header>

        <div className="flex flex-col items-center gap-4 px-6 py-6">
          <div
            className="relative overflow-hidden rounded-full border border-border bg-paper touch-none"
            style={{ width: FRAME, height: FRAME }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {src ? (
              <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                onLoad={(e) =>
                  setNatural({
                    w: e.currentTarget.naturalWidth,
                    h: e.currentTarget.naturalHeight,
                  })
                }
                className="absolute select-none cursor-grab active:cursor-grabbing"
                style={{ left: topLeftX, top: topLeftY, width: w, height: h, maxWidth: 'none' }}
              />
            ) : null}
          </div>

          <label className="flex w-full items-center gap-3">
            <span className="text-[12px] text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={!natural}
              className="flex-1 accent-icaria-600"
              aria-label="Zoom de la foto"
            />
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm} disabled={busy || !natural}>
            {busy ? 'Guardando…' : 'Guardar foto'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
